import { useEffect, useState } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import {
    RenderComponentResolver,
    UniformComposition,
    UniformPlayground
} from "@uniformdev/canvas-react";
import { Quirks } from "@uniformdev/context";
import { useUniformContext } from "@uniformdev/context-react";

import { expectContent } from "@/content/utils/expectContent";

import { enhanceComposition } from "../clients/uniform-client/enhancers";
import { useUserContext } from "../components/user-context";
import { parseRoute } from "../wac-routes";

import UNIFORM_DEFINITIONS from "./uniform-definitions";
import { UniformMissingComponent } from "./utils/uniform-missing-component";

declare global {
    interface Window {
        _uniformPreloadedComposition?: RootComponentInstance;
    }
}

export type UniformWrapperProps = {
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
    isAllowPostMessage?: boolean;
};

const resolveRenderer: RenderComponentResolver = ({ type }) => {
    const MissingComponent = () => (
        <UniformMissingComponent componentName={type} />
    );
    return UNIFORM_DEFINITIONS[type] ?? MissingComponent;
};

export const UniformWrapper = ({
    composition,
    isUniformPlayground,
    isAllowPostMessage
}: UniformWrapperProps) => {
    const preloadedComposition =
        typeof window !== "undefined"
            ? window._uniformPreloadedComposition
            : null;

    const initialData = preloadedComposition || composition;

    const [data, setData] = useState(initialData);
    const [enhancedData, setEnhancedData] = useState(initialData);
    const [isFetched, setIsFetched] = useState(!!initialData);

    // Listen for postMessage events to update composition dynamically
    // Only enabled if allowPostMessage flag is set and in non-production environments
    useEffect(() => {
        if (typeof window === "undefined") return;

        // Only enable if allowPostMessage flag is set
        if (!isAllowPostMessage) return;

        const handleMessage = async (event: MessageEvent) => {
            // Check if message is for updating Uniform composition
            if (
                event.data?.type === "UNIFORM_COMPOSITION_UPDATE" &&
                event.data?.composition
            ) {
                try {
                    const compositionData = event.data.composition;
                    // If composition is wrapped in compositionApiResponse, extract it
                    const rawComposition =
                        compositionData.compositionApiResponse?.composition ||
                        compositionData;

                    // Enhance the composition
                    const enhancedComp = await enhanceComposition(
                        rawComposition as RootComponentInstance
                    );

                    // Update state to trigger re-render
                    setData(enhancedComp);
                    setIsFetched(true);

                    console.log("Uniform composition updated via postMessage");
                } catch (error) {
                    console.error(
                        "Error processing composition from postMessage:",
                        error
                    );
                }
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [isAllowPostMessage]);

    useEffect(() => {
        if (initialData) return;

        const fetchComposition = async () => {
            if (typeof window === "undefined") return;

            const { path, locale } = parseRoute(window.location);

            if (path === "uniform-preview" && isAllowPostMessage === true) {
                console.log(
                    "uniform-preview with allowPostMessage - waiting for postMessage"
                );
                return;
            }

            const slug = path.startsWith("/") ? path.substring(1) : path;

            const cdn = process.env["UNIFORM_CDN"];
            const cdn_path = `${cdn}release/${locale}/${slug}/composition.json`;

            try {
                const req = await fetch(cdn_path);
                const file = await req.json();
                const enhancedComp = await enhanceComposition(
                    file.compositionApiResponse.composition
                );
                setData(enhancedComp);
            } catch (error) {
                console.error("Error fetching composition from CDN:", error);
                if (composition) {
                    setData(composition);
                }
            }
            setIsFetched(true);
        };

        fetchComposition();
    }, [initialData, composition, isAllowPostMessage]);

    const { user } = useUserContext();
    const { context } = useUniformContext();

    useEffect(() => {
        const tokens = new Map([["{{name}}", user?.name || ""]]);
        let jsonString = JSON.stringify(data);
        if (jsonString) {
            jsonString = jsonString.replace(/\{\{[a-zA-Z-]*\}\}/g, (match) => {
                return tokens.get(match) || match;
            });
            setEnhancedData(JSON.parse(jsonString));
        }

        const updateQuirks = async () => {
            const quirks: Quirks = {};
            if (user?.name) {
                quirks["name"] = user.name;
                await context.update({ quirks });
            }
        };

        updateQuirks();
    }, [user?.name, context, data]);

    if (isUniformPlayground) {
        return (
            <UniformPlayground
                resolveRenderer={resolveRenderer}
                contextualEditingEnhancer={({ composition }) =>
                    enhanceComposition(composition)
                }
            />
        );
    }

    return (
        isFetched && (
            <div>
                <UniformComposition
                    data={expectContent(enhancedData as RootComponentInstance)}
                    behaviorTracking="onLoad"
                    resolveRenderer={resolveRenderer}
                    contextualEditingEnhancer={({ composition }) =>
                        enhanceComposition(composition)
                    }
                />
            </div>
        )
    );
};
